import { z } from "zod";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAudioSlot } from "@/features/audio/kiosk-audio";

export function useApiErrorHandler() {
  const { showModal } = useMuiModal();

  return async <T extends z.ZodTypeAny | undefined>(
    res: Response,
    schema: T,
    errorContainer?: {
      errorKey?: string;
      errorDetails?: string | null;
      translatedMessage?: string;
    },
  ) => {
    const result = await parseResponse(res, schema);

    if (result.type === "error") {
      if (errorContainer) {
        errorContainer.errorKey = result.error;
        errorContainer.errorDetails = result.errorDetails;
        errorContainer.translatedMessage = result.translatedMessage;
      }
      showModal(
        <ErrorModal
          errorKey={result.error}
          errorDetails={result.errorDetails}
          translatedMessage={result.translatedMessage}
        />,
      );
      return null;
    }

    return result.data;
  };
}

export async function parseResponse<T extends z.ZodTypeAny | undefined>(
  res: Response,
  schema: T,
) {
  const result = await (res.ok
    ? schema === undefined
      ? { data: undefined }
      : res
          .json()
          .catch(() => undefined)
          .then((data) => ({
            type: "success" as const,
            data:
              schema === undefined
                ? undefined
                : (schema.parse(data) as z.infer<
                    T extends z.ZodTypeAny ? T : never
                  >),
          }))
          .catch((err) => {
            console.error(err);
            return {
              error: "server-message-internal-server-error" as const,
              errorDetails: undefined,
              translatedMessage: undefined,
            };
          })
    : res
        .json()
        .then(async (res) => {
          const parsed = errorSchema.parse(res);
          return {
            error: parsed.message,
            errorDetails: parsed.errorDetails,
            translatedMessage: parsed.translatedMessage,
          };
        })
        .catch(
          () =>
            ({
              error: "server-message-internal-server-error",
              errorDetails: undefined,
              translatedMessage: undefined,
            }) as const,
        ));
  if ("error" in result) {
    return {
      type: "error" as const,
      error: result.error,
      errorDetails: result.errorDetails,
      translatedMessage: result.translatedMessage,
    };
  }
  return { type: "success" as const, data: result.data };
}

// Keys the kiosk still translates itself. Coupon validation errors are NOT
// listed here on purpose: the backend sends them with a `translatedMessage`,
// which takes precedence over key-based translation in ErrorModal.
const knownErrorKeys = z.enum([
      "server_message_user_already_exist",
      "server_message_database_error_on_save",
      "server_message_database_error_on_delete",
      "server_message_insufficient_user_balance",
      "server_message_coupon_not_updated",
      "server_message_finance_event_not_created",
      "server_message_finance_event_id_not_builded",
      "server_message_finance_event_fetching_error",
      "server_message_reservation_time_inactive",
      "server_message_reservation_unpaid",
      "server_message_machine_not_found",
      "server_message_machine_fetching_error",
      "server_message_machine_is_in_use",
      "server_message_machine_is_reserved",
      "server_message_socket_not_found",
      "server_message_input_data_not_found",
      "server_message_output_data_not_found",
      "server_message_payment_error",
      "server_message_payment_not_supported",
      "server_message_reservation_already_used",
      "server_message_reservation_id_not_exist",
      "server_message_user_permitted",
      "server_message_reservation_not_exist",
      "server_message_invalid_reservation_id",
      "server_message_reservation_already_paid",
      "server_message_reservation_timeout",
      "server_message_user_already_has_reservation",
      "server_message_cannot_reserve_to_shop",
      "server_message_invalid_date_for_reservation",
      "server_message_reservation_too_late",
      "server_message_shop_is_closed",
      "server_message_reservation_fetching_error",
      "server_message_reservation_fetching_with_machine_error",
      "server_message_reservation_creation_error",
      "server_message_reservation_update_error",
      "server_message_reservation_fetching_with_machine_for_one_day_error",
      "server_message_cannot_count_reservations_by_user",
      "server_message_cannot_count_reservations_by_machine",
      "server_message_cannot_delete_reservation",
      "server_message_cannot_fetch_runs_by_machines",
      "server_message_cannot_fetch_runs_by_user",
      "server_message_invalid_date",
      "server_message_no_shops_available",
      "server_message_shop_not_exist",
      "server_message_shop_locale_not_exist",
      "server_message_no_data",
      "server_message_cannot_find_cities",
      "server_message_cannot_create_transaction",
      "server_message_cannot_update_transaction",
      "server_message_value_must_be_boolean",
      "server_message_unknown_setting_id",
      "server_message_cannot_update_user",
      "server_message_user_is_locked",
      "server_message_history_not_found",
      "server_message_kiosk_payment_nonce_fetching_error",
      "server_message_kiosk_payment_nonce_not_found",
      "server_message_payment_type_fetching_error",
      "server_message_client_token_fetching_error",
      "server_message_terminal_communication_error",
      "server_message_terminal_payment_error",
      "server_message_terminal_unreachable",
      "server_message_hopper_timed_out",
      "server_message_hopper_payment_aborted",
      "server_message_hopper_top_up_aborted",
      "server_message_hopper_payment_succeeded",
      "server_message_hopper_top_up_succeeded",
      "server_message_hopper_top_up_zero_amount",
      "server_message_hopper_payment_error",
      "server_message_hopper_top_up_error",
      "server_message_hopper_cash_sync_error",
      "server_message_device_not_found",
      "server_message_kiosk_not_found",
      "server_message_favourites_not_found",
      "server_message_coupon_nonce_fetching_error",
      "server_message_user_fetching_error",
      "server_message_user_not_found",
      "server_message_email_not_sent",
      "server_message_billing_data_fetching_error",
      "server_message_cannot_update_billing_info",
      "server_message_contract_fetching_error",
      "server_message_contract_not_found",
      "server_message_invoice_fetching_error",
      "server_message_moxa_fetching_error",
      "server_message_pricing_rule_fetching_error",
      "server_message_machine_qr_fetching_error",
      "server_message_cannot_build_run_id",
      "server_message_cannot_build_session_id",
      "server_message_pictogram_fetching_error",
      "server_message_shop_fetching_error",
      "server_message_shop_locale_fetching_error",
      "server_message_locale_settings_fetching_error",
      "server_message_shop_qr_fetching_error",
      "server_message_transaction_fetching_error",
      "server_message_favourites_fetching_error",
      "server_message_terms_and_confitions_fetching_error",
      "server_message_newest_contracts_not_accepted",
      "server_message_moxa_communication_error",
      "server_message_moxa_communication_error_get_pulse_status",
      "server_message_cash_payment_hopper_enable_failed",
      "server_message_cash_payment_nv_enable_failed",
      "server_message_cash_payment_adjust_maximum_note_failed",
      "server_message_cash_payment_set_minimum_coin_failed",
      "server_message_cash_payment_already_enabled_or_dispensing",
      "server_message_cash_payment_malfunction",
      "server_message_cash_payment_not_allowed",
      "server_message_cash_top_up_not_allowed",
      "server_message_bank_card_declined",
      "server_message_bank_card_payment_not_allowed",
      "server_message_balance_payment_not_allowed",
      "server_message_shop_pop_up_not_found",
      "server_message_media_not_found",
      "server_message_shop_pop_up_fetching_error",
      "server_message_media_fetching_error",
      "server_message_command_fetching_error",
      "server_message_franchise_data_fetching_error",
      "server_message_franchise_data_not_found",
      "server_message_cannot_generate_pin_code",
      "server_message_unable_to_access_id_store",
      "server_message_maximum_id_fetching_error",
      "server_message_latest_payment_does_not_exist",
      "server_message_hopper_query_stat_error",
      "server_message_machine_is_out_of_order",
      "server_message_cannot_build_credit_card_transaction_id",
      "server_message_user_not_confirmed",
      "server_message_username_already_exists",
      "server_message_email_already_in_use",
      "server_message_user_credentials_already_migrated",
      "server_message_user_invalid_credentials",
      "server_message_email_address_not_found",
]);

export const errorSchema = z
  .object({
    message: z.string().nullish(),
    // Already-localized message rendered by the backend from the lang-key header.
    translatedMessage: z.string().nullish(),
    errorDetails: z.string().nullish(),
  })
  .transform(({ message, translatedMessage, errorDetails }) => {
    const known = knownErrorKeys.safeParse(message);
    return {
      message: (known.success
        ? known.data
        : "server_message_internal_server_error"
      ).replace(/_/g, "-"),
      translatedMessage: translatedMessage ?? undefined,
      errorDetails,
    };
  });

export const ErrorModal = ({
  errorKey,
  errorDetails,
  translatedMessage,
}: {
  errorKey: z.infer<typeof errorSchema>["message"];
  errorDetails?: string | null;
  translatedMessage?: string | null;
}) => {
  useAudioSlot("modal.error");
  const { t } = useTranslation();
  const { closeModal } = useMuiModal();
  return (
    <Modal>
      <ModalTitle>
        <Trans>Hoppá!</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>{translatedMessage ?? t(errorKey)}</p>
        {errorDetails && (
          <p className={`leading-6 mb-12 text-sm text-gray-600`}>
            {t(errorDetails, { defaultValue: errorDetails })}
          </p>
        )}
        <Button onClick={closeModal}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};
